import { Injectable, inject } from '@angular/core';
import {
  ExportContext,
  ExportFileFormat,
  ExportRange,
  ExportService,
} from 'app/layout/export-overlay';
import { BusinessPartnerService } from 'app/modules/components/business-partners/business-partner/business-partner.service';
import ceil from 'lodash-es/ceil';
import { takeUntil } from 'rxjs';
import { computeFrequencyData } from '../../../helpers/frequency.helpers';
import { BusinessPartnerBySubRoute } from '../../../interfaces/busines-partner-by-sub-route.interface';
import { AssignedPartnersListService } from '../assigned-partners-list.service';
import {
  ASSIGNED_PARTNERS_EXPORT_ID,
  ASSIGNED_PARTNERS_LOCALIZED_PROPERTIES,
  assignedPartnersExportTitle,
} from './assigned-partners-list.constants';
import { formatUseGpsMode } from '../../permission-gps.constants';

@Injectable()
export class AssignedPartnersListExport {
  private exportService = inject(ExportService);
  private businessPartnerService = inject(BusinessPartnerService);
  private listService = inject(AssignedPartnersListService);

  private transformItem(item: BusinessPartnerBySubRoute): Record<string, unknown> {
    const frequencyData = computeFrequencyData(item.frequenciesFromThisSubRoute);
    return {
      name: item.name ?? '',
      externalCode: item.externalKeys?.code ?? '',
      address: item.principalAddress?.address ?? '',
      useGpsMode: formatUseGpsMode(item.useGpsMode),
      contact: item.principalContact?.fullName ?? '',
      phone: item.principalPhone
        ? `${item.principalPhone.prefix ?? ''}-${item.principalPhone.phone ?? ''}`
        : '',
      email: item.principalContact?.emails?.[0]?.email ?? '',
      daysText: frequencyData.bpDaysInAllFrecuencies,
      dayFrequencies: frequencyData.bpDayFrecuenciesLength,
      customFrequencies: frequencyData.bpCustomFrecuenciesLength,
    };
  }

  export(
    format: ExportFileFormat,
    range: ExportRange,
    totalItems: number,
    pageSize: number,
    pageIndex: number
  ): void {
    const subRouteId = this.listService.subRouteId();
    const currentPagination = this.listService.pagination();

    if (!subRouteId) return;

    const totalPages = ceil(totalItems / pageSize) + 1;

    this.exportService
      .task({
        id: ASSIGNED_PARTNERS_EXPORT_ID,
        title: assignedPartnersExportTitle(),
        format: format,
        iterations: range === ExportRange.AllPages ? totalPages : 1,
        start: (ctx: ExportContext) => {
          ctx.processor.localizeItemProperties(
            assignedPartnersExportTitle(),
            ASSIGNED_PARTNERS_LOCALIZED_PROPERTIES
          );
        },
        next: (ctx: ExportContext) => {
          const page = range === ExportRange.AllPages ? ctx.iteration : pageIndex + 1;

          // Keep same filters (search, sort, sortDirection), only override pagination
          const exportPagination = {
            ...currentPagination,
            currentPage: page,
            limitPerPage: pageSize,
          };

          this.businessPartnerService
            .findAssignedToSubRouteBusinessPartnerWithCount(
              {},
              exportPagination,
              subRouteId,
              this.listService.subRouteData()?.company?._id?.toString() ?? null
            )
            .pipe(takeUntil(ctx.stopped))
            .subscribe((response) => {
              response.data.data.forEach((item) => {
                ctx.processor.addToSection(
                  assignedPartnersExportTitle(),
                  this.transformItem(item)
                );
              });
              ctx.next();
            });
        },
      })
      .start();
  }

  canStartTask(): boolean {
    if (this.exportService.taskIsActive(ASSIGNED_PARTNERS_EXPORT_ID)) {
      this.exportService.setVisible(true);
      return false;
    }
    return true;
  }
}
